import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { ShortModel } from '@data-layer/models/common';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarineInsuranceHelperService } from '../../services';
import { enumInsuranceShipmentType, enumMarineInsuranceCommodity, enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceInsuredValueModel, MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { BillItemPendingModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { marineInsuranceAmountMaxLimits } from '../../constants/marine-insurance.const';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumPaymentState } from '@data-layer/enums/payments';

@Component({
  selector: 'app-marine-insurance-sidebar-quote-management-form',
  templateUrl: './marine-insurance-sidebar-quote-management-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-quote-management-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarQuoteManagementFormComponent implements OnInit, OnDestroy {
  @Input() public entity: enumMarineInsuranceEntityType;
  @Input() public marineInsurance: MarineInsuranceManagementModel;
  @Input() public pendingBills: PaymentBillItemPendingModel;
  @Input() public isShowNewPayment: boolean;
  @Input() public isSaving: boolean;
  @Input() public commodities: ShortModel[];

  @Output() public discard: EventEmitter<void> = new EventEmitter<void>();
  @Output() public recalculate: EventEmitter<MarineInsuranceInsuredValueModel> = new EventEmitter<MarineInsuranceInsuredValueModel>();
  @Output() public save: EventEmitter<void> = new EventEmitter<void>();

  public formGroup: UntypedFormGroup;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public enumInsuranceShipmentType: typeof enumInsuranceShipmentType = enumInsuranceShipmentType;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public errors: { [name: string]: IErrorForm } = {
    insuredAmount: {
      required: 'FORM_ERROR.REQUIRED',
      min: 'MARINE_INSURANCE_LIMITED_UP_FROM',
      max: 'MARINE_INSURANCE_LIMITED_UP_TO',
    },
  };

  public cifAdded: boolean;
  public cifPercentage: number = 10;
  public isOpenedDescription: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private marineInsuranceHelperService: MarineInsuranceHelperService, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      insuredAmount: new UntypedFormControl(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(marineInsuranceAmountMaxLimits[enumMarineInsuranceCommodity.GENERAL]),
      ]),
      commodityId: new UntypedFormControl(enumMarineInsuranceCommodity.GENERAL, Validators.required),
      typeOfShipment: new UntypedFormControl(enumInsuranceShipmentType.COLOADING),
      goodsTypeDescription: new UntypedFormControl(null),
    });

    this.formGroup
      .get('commodityId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: enumMarineInsuranceCommodity) => {
        if (val === enumMarineInsuranceCommodity.VEHICLES) {
          this.formGroup.get('typeOfShipment').setValue(enumInsuranceShipmentType.BREAKBULK);
          this.formGroup.get('typeOfShipment').setValidators(Validators.required);
        } else {
          this.formGroup.get('typeOfShipment').setValidators(null);
        }

        this.formGroup
          .get('insuredAmount')
          .setValidators([Validators.required, Validators.min(1), Validators.max(this.insuredAmountLimit)]);
        this.formGroup.get('insuredAmount').updateValueAndValidity();

        this.isOpenedDescription = false;
      });

    if (!!this.marineInsurance) {
      this.formGroup.patchValue({
        insuredAmount: this.marineInsurance?.insuredValue?.insuredAmount,
        commodityId: this.marineInsurance?.insuredValue?.commodity?.id,
        typeOfShipment: this.marineInsurance?.insuredValue?.typeOfShipment,
        goodsTypeDescription:
          this.marineInsurance?.updatedInsuredValue?.goodsTypeDescription || this.marineInsurance?.insuredValue?.goodsTypeDescription,
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public addCif(): void {
    if (!this.cifAdded && Number(this.formGroup.get('insuredAmount').value) > 0) {
      this.formGroup.get('insuredAmount').setValue(this.formGroup.get('insuredAmount').value * (1 + this.cifPercentage / 100));
      this.cifAdded = true;
    }
  }

  public setOpenedDescription(): void {
    this.isOpenedDescription = !this.isOpenedDescription;
  }

  public getCommodityDescription(commodityIdId: number): string {
    return this.marineInsuranceHelperService.getCommodityDescription(commodityIdId);
  }

  public isShowMore(commodityIdId: number): boolean {
    return commodityIdId === enumMarineInsuranceCommodity.GENERAL;
  }

  public isShowTransportationMode(commodityIdId: number): boolean {
    return commodityIdId === enumMarineInsuranceCommodity.VEHICLES;
  }

  public onRecalculate(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.valid) {
      this.isSaving = true;
      this.recalculate.emit(this.formGroup.getRawValue());
    }
  }

  public onDiscard(): void {
    this.discard.emit();
  }

  public onSave(): void {
    this.isSaving = true;
    this.save.emit();
  }

  public detectChanges(): void {
    this.cdr.markForCheck();
  }

  public get totalPrevSum(): number {
    return this.marineInsurance.certificate?.handlingFee + this.marineInsurance.certificate?.providerCost;
  }

  public get totalCurrSum(): number {
    return this.marineInsurance.certificate?.updatedHandlingFee + this.marineInsurance.certificate?.updatedProviderCost;
  }

  public get totalDiffSum(): number {
    return this.totalCurrSum - this.totalPrevSum;
  }

  public get saveText(): string {
    if (this.totalDiffSum === 0) {
      return 'BUTTON.SAVE';
    }

    return this.totalDiffSum > 0 ? 'APPLY_NEW_PAYMENT_REQUEST' : 'PROCESS_PARTIAL_REFUND';
  }

  public get insuredAmountLimit(): number {
    return (
      marineInsuranceAmountMaxLimits[this.formGroup.get('commodityId').value] ||
      marineInsuranceAmountMaxLimits[enumMarineInsuranceCommodity.GENERAL]
    );
  }

  public get joinedItems(): BillItemPendingModel[] {
    return this.marineInsuranceHelperService.getJoinedItems(this.pendingBills?.items);
  }

  public get isPaymentMade(): boolean {
    return [enumPaymentState.SUCCESS, enumPaymentState.REFUNDED].includes(this.marineInsurance?.updatedPaymentState);
  }

  public get isPaymentApplied(): boolean {
    return (
      (!!this.marineInsurance?.updatedInsuredValue?.insuredAmount && !!this.marineInsurance?.pendingPaymentAction) || this.isPaymentMade
    );
  }
}
